import { useActivityShowContext } from "PFApp/activities/show/context/activity_show_context";
import { RoleWatcher } from "PFApp/activities/types/watchers";
import ProfileCell from "PFApp/components/profile_cell/profile_cell";
import { useProfilePreview } from "PFApp/use_profile_preview";
import { Markdown } from "PFComponents/markdown";
import { useDateFormatter } from "PFCore/hooks";
import { useActivityInvalidate } from "PFCore/hooks/queries/activity";
import { TableColumn } from "PFTypes";
import { Profile } from "PFTypes";
import { useCallback, useMemo } from "react";

import AddShortlistButton from "../../../../../parts/add_shortlist_button";
import MatchValueTooltipped from "../interested_table_cells/match_value";
import { Questions } from "../interested_table_cells/questions";

export const useCellFactory = () => {
  const { formatDate, formatDateTime } = useDateFormatter();

  const { activityId } = useActivityShowContext();
  const { invalidateWatchers } = useActivityInvalidate();
  const { showPreview } = useProfilePreview();

  const handleProfileClick = useCallback(
    (watcher: RoleWatcher) => {
      showPreview({
        id: watcher.profile.id,
        profile: watcher.profile as unknown as Profile
      });
    },
    [showPreview]
  );

  const cellRenderers = useMemo(
    () => ({
      "name": ({ row }) => (
        <ProfileCell profile={row.original.profile} onClick={() => handleProfileClick(row.original)} />
      ),
      "profile_id": ({ row }) => row.original.profile.id,
      "questions": ({ row }) => <Questions watcher={row.original} />,
      "statement": ({ getValue }) => <Markdown raw={getValue() || ""} crop={150} emojify={false} />,
      "shortlist": ({ row }) => (
        <AddShortlistButton
          profile={row.original.profile}
          onClick={() => invalidateWatchers(activityId)}
          named
        />
      ),
      "date": ({ getValue }) => getValue() && formatDate(getValue()),
      "date-time": ({ getValue }) => getValue() && formatDateTime(getValue()),
      "availabilityScore": ({ row }) => (
        <MatchValueTooltipped scoreName="availabilityScore" scores={row.original.scores} />
      ),
      "normalizedScore": ({ row }) => (
        <MatchValueTooltipped scoreName="normalizedScore" scores={row.original.scores} />
      )
    }),
    [formatDate, formatDateTime, activityId, handleProfileClick, invalidateWatchers]
  );
  const defaultRenderer = ({ getValue }) => getValue() && String(getValue());

  return (column: TableColumn) =>
    cellRenderers[column.name] || cellRenderers[column.type!] || defaultRenderer;
};
